class ShrineSubscriptionWidget extends HTMLElement {
  constructor() {
    super();
    this.handleBoxClick = this.handleBoxClick.bind(this);
    this.handleFrequencyChange = this.handleFrequencyChange.bind(this);
    this.handleQuantityBreakChange = this.handleQuantityBreakChange.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.boxes = [];
    this.frequencyInputs = [];
  }

  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.productForm = document.getElementById(`product-form-${this.dataset.section}`);
    this.boxes = Array.from(this.querySelectorAll('.subscription__box'));
    this.subscriptionBox = this.querySelector('.subscription__box--subscription');
    this.frequencySelect = this.querySelector('.subscription__select select');
    this.frequencyInputs = Array.from(this.querySelectorAll('.subscription__frequency-input'));
    this.subscriptionPriceElements = Array.from(this.querySelectorAll('.sub-price-update'));
    this.quantityBreaks = document.getElementById(`quantity-breaks-${this.dataset.section}`);
    this.unsubscribeVariantChange =
      typeof subscribe === 'function' ? subscribe('variant-change', this.handleVariantChange) : null;

    if (this.dataset.available === 'false') {
      this.removeSellingPlan();
      return;
    }

    this.boxes.forEach((box) => box.addEventListener('click', this.handleBoxClick));
    this.frequencySelect?.addEventListener('change', this.handleFrequencyChange);
    this.frequencyInputs.forEach((input) => input.addEventListener('change', this.handleFrequencyChange));
    this.quantityBreaks?.addEventListener('change', this.handleQuantityBreakChange);

    this.setSelection(this.dataset.preselected === 'subscription' ? 'subscription' : 'one_time');
  }

  disconnectedCallback() {
    this.boxes.forEach((box) => box.removeEventListener('click', this.handleBoxClick));
    this.frequencySelect?.removeEventListener('change', this.handleFrequencyChange);
    this.frequencyInputs.forEach((input) => input.removeEventListener('change', this.handleFrequencyChange));
    this.quantityBreaks?.removeEventListener('change', this.handleQuantityBreakChange);
    this.unsubscribeVariantChange?.();
    this.initialized = false;
  }

  handleBoxClick(event) {
    if (event.target.closest?.('.subscription__frequency')) return;

    const box = event.currentTarget;
    const isSelectedCheckbox =
      this.dataset.isCheckbox === 'true' &&
      box.dataset.type === 'subscription' &&
      box.dataset.selected === 'true';

    this.setSelection(isSelectedCheckbox ? 'one_time' : box.dataset.type);
  }

  handleFrequencyChange() {
    this.setSelection('subscription');
  }

  handleQuantityBreakChange() {
    this.updateSubscriptionPriceText();
    this.updateLinkedPrices(this.subscriptionBox?.dataset.selected === 'true');
  }

  handleVariantChange({ data } = {}) {
    if (data?.sectionId !== this.dataset.section) return;

    const replacement = data.html?.querySelector?.(`#SubscriptionWidget-${this.dataset.section}`);
    if (!replacement) return;

    replacement.dataset.preselected =
      this.subscriptionBox?.dataset.selected === 'true' ? 'subscription' : 'one_time';

    if (replacement.dataset.available === 'false') {
      this.removeSellingPlan();
    }

    this.replaceWith(replacement);
  }

  setSelection(type) {
    const subscriptionSelected = type === 'subscription';

    this.boxes.forEach((box) => {
      const selected =
        box.dataset.type === 'subscription' ? subscriptionSelected : !subscriptionSelected;

      box.dataset.selected = selected ? 'true' : 'false';
      box.classList.toggle('subscription__box--selected', selected);
    });

    if (subscriptionSelected) {
      this.syncSellingPlan();
    } else {
      this.removeSellingPlan();
    }

    this.updateSubscriptionPriceText();
    this.updateLinkedPrices(subscriptionSelected);
  }

  getSelectedFrequency() {
    if (this.frequencySelect) {
      return (
        this.frequencySelect.selectedOptions?.[0] ||
        this.frequencySelect.options?.[this.frequencySelect.selectedIndex] ||
        null
      );
    }

    return this.frequencyInputs.find((input) => input.checked) || this.frequencyInputs[0] || null;
  }

  getSellingPlanId() {
    const frequency = this.getSelectedFrequency();
    return frequency?.dataset.sellingPlanId || frequency?.value || this.dataset.firstSellingPlanId;
  }

  syncSellingPlan() {
    if (!this.productForm) return;

    const sellingPlanId = this.getSellingPlanId();
    if (!sellingPlanId) return;

    let input = this.productForm.querySelector('input[name="selling_plan"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      input.dataset.shrineSubscriptionPlan = 'true';
      this.productForm.appendChild(input);
    }

    input.disabled = false;
    input.value = sellingPlanId;
  }

  removeSellingPlan() {
    if (!this.productForm) return;

    const input = this.productForm.querySelector('input[name="selling_plan"]');
    if (!input) return;

    if (input.dataset.shrineSubscriptionPlan === 'true') {
      input.remove();
    } else {
      input.disabled = true;
    }
  }

  updateSubscriptionPriceText() {
    const frequency = this.getSelectedFrequency();
    const price = Number(frequency?.dataset.price);
    const comparePrice = Number(frequency?.dataset.comparePrice);

    if (!Number.isFinite(price) || !Number.isFinite(comparePrice)) return;

    const displayedPrice = this.getDisplayedSubscriptionPrice(price, comparePrice);
    const amountSaved = Math.max(displayedPrice.comparePrice - displayedPrice.price, 0);
    const percentageSaved =
      displayedPrice.comparePrice > 0
        ? Math.floor((amountSaved * 100) / displayedPrice.comparePrice)
        : 0;
    const values = {
      '[amount_saved]': this.formatMoney(amountSaved),
      '[amount_saved_rounded]': this.formatMoney(Math.round(amountSaved / 100) * 100),
      '[compare_price]': this.formatMoney(displayedPrice.comparePrice),
      '[compare_price_each]': this.formatMoney(displayedPrice.comparePriceEach),
      '[percentage_saved]': `${percentageSaved}%`,
      '[price]': this.formatMoney(displayedPrice.price),
      '[price_each]': this.formatMoney(displayedPrice.priceEach),
      '[quantity]': displayedPrice.quantity,
      '[subscription_name]': this.dataset.subscriptionName || '',
    };

    this.subscriptionPriceElements.forEach((element) => {
      if (!element.dataset.text) return;

      element.innerHTML = Object.entries(values).reduce(
        (text, [placeholder, value]) => text.split(placeholder).join(value),
        element.dataset.text,
      );
    });
  }

  getDisplayedSubscriptionPrice(price, comparePrice) {
    const displayedPrices = this.dataset.displayedPrices || 'unit';
    const quantityBreak = this.getSelectedQuantityBreak();

    if (displayedPrices === 'unit' || !quantityBreak) {
      return {
        comparePrice,
        comparePriceEach: comparePrice,
        price,
        priceEach: price,
        quantity: 1,
      };
    }

    const basePrice = Number(this.dataset.basePrice);
    const quantity = Number(quantityBreak.dataset.quantity);
    const percentageLeft = Number(quantityBreak.dataset.percentageLeft);
    const fixedDiscount = Number(quantityBreak.dataset.fixedDiscount);

    if (
      !Number.isFinite(basePrice) ||
      basePrice <= 0 ||
      !Number.isFinite(quantity) ||
      quantity <= 0 ||
      !Number.isFinite(percentageLeft) ||
      !Number.isFinite(fixedDiscount)
    ) {
      return {
        comparePrice,
        comparePriceEach: comparePrice,
        price,
        priceEach: price,
        quantity: 1,
      };
    }

    const priceMultiplier = price / basePrice;
    const totalPrice = Math.max((basePrice * quantity * percentageLeft - fixedDiscount) * priceMultiplier, 0);
    const totalComparePrice = comparePrice * quantity;
    const total = {
      comparePrice: totalComparePrice,
      comparePriceEach: totalComparePrice / quantity,
      price: totalPrice,
      priceEach: totalPrice / quantity,
      quantity,
    };

    if (displayedPrices === 'total') return total;

    return {
      comparePrice: total.comparePriceEach,
      comparePriceEach: total.comparePriceEach,
      price: total.priceEach,
      priceEach: total.priceEach,
      quantity,
    };
  }

  getSelectedQuantityBreak() {
    if (!this.quantityBreaks) return null;

    return (
      this.quantityBreaks.querySelector('input[name="quantity"]:checked + .quantity-break') ||
      this.quantityBreaks.querySelector('.quantity-break')
    );
  }

  updateLinkedPrices(subscriptionSelected) {
    if (this.dataset.updatePrices !== 'true') return;

    const basePrice = Number(this.dataset.basePrice);
    const baseComparePrice = Number(this.dataset.baseComparePrice);
    const frequency = this.getSelectedFrequency();
    const subscriptionPrice = Number(frequency?.dataset.price);
    const subscriptionComparePrice = Number(frequency?.dataset.comparePrice);
    const price =
      subscriptionSelected && Number.isFinite(subscriptionPrice) ? subscriptionPrice : basePrice;
    const comparePrice =
      subscriptionSelected && Number.isFinite(subscriptionComparePrice)
        ? subscriptionComparePrice
        : baseComparePrice;

    if (!Number.isFinite(price) || !Number.isFinite(comparePrice)) return;

    this.updateMainPrices(price, comparePrice);
    this.updateQuantityBreakPrices(price, basePrice);
  }

  updateMainPrices(price, comparePrice) {
    document.querySelectorAll('.main-price').forEach((element) => {
      element.innerHTML = this.formatMoney(price);
      element.closest?.('.price')?.classList.toggle('price--on-sale', comparePrice > price);
    });

    document.querySelectorAll('.main-comapre-price').forEach((element) => {
      element.innerHTML = this.formatMoney(comparePrice);
      element.classList.toggle('hidden', comparePrice <= price);
    });
  }

  updateQuantityBreakPrices(price, basePrice) {
    const quantityBreaks = document.getElementById(`quantity-breaks-${this.dataset.section}`);
    if (!quantityBreaks || !Number.isFinite(basePrice) || basePrice <= 0) return;

    const priceMultiplier = price / basePrice;

    quantityBreaks.querySelectorAll('.quantity-break').forEach((quantityBreak) => {
      const quantity = Number(quantityBreak.dataset.quantity);
      const baseComparePrice = Number(quantityBreak.dataset.baseComparePrice);
      const percentageLeft = Number(quantityBreak.dataset.percentageLeft);
      const fixedDiscount = Number(quantityBreak.dataset.fixedDiscount);

      if (
        !Number.isFinite(quantity) ||
        !Number.isFinite(baseComparePrice) ||
        !Number.isFinite(percentageLeft) ||
        !Number.isFinite(fixedDiscount)
      ) {
        return;
      }

      const totalPrice = Math.max((basePrice * quantity * percentageLeft - fixedDiscount) * priceMultiplier, 0);
      const totalComparePrice = baseComparePrice * quantity;
      const amountSaved = Math.max(totalComparePrice - totalPrice, 0);
      const percentageSaved =
        totalComparePrice > 0 ? Math.floor((amountSaved * 100) / totalComparePrice) : 0;
      const values = {
        '[amount_saved]': this.formatMoney(amountSaved),
        '[amount_saved_rounded]': this.formatMoney(Math.round(amountSaved / 100) * 100),
        '[compare_price]': this.formatMoney(totalComparePrice),
        '[compare_price_each]': this.formatMoney(totalComparePrice / quantity),
        '[percentage_saved]': `${percentageSaved}%`,
        '[price]': this.formatMoney(totalPrice),
        '[price_each]': this.formatMoney(totalPrice / quantity),
        '[quantity]': quantity,
      };

      quantityBreak.querySelectorAll('.variant-price-update').forEach((element) => {
        if (!element.dataset.text) return;

        element.innerHTML = Object.entries(values).reduce(
          (text, [placeholder, value]) => text.split(placeholder).join(value),
          element.dataset.text,
        );
      });

      quantityBreak
        .querySelector('.quantity-break__compare-price')
        ?.classList.toggle('hidden', totalComparePrice <= totalPrice);
    });
  }

  formatMoney(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!placeholder) return format;

    const formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_with_space_separator: [2, ' ', ','],
    };
    const [precision, thousands, decimal] = formats[placeholder[1]] || formats.amount;
    const value = this.formatWithDelimiters(cents, precision, thousands, decimal);

    return format.replace(placeholder[0], value);
  }

  formatWithDelimiters(cents, precision, thousands, decimal) {
    const value = (Number(cents) / 100).toFixed(precision);
    const [whole, fraction] = value.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  }
}

if (!customElements.get('shrine-subscription-widget')) {
  customElements.define('shrine-subscription-widget', ShrineSubscriptionWidget);
}
